import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../Modal';
import './OrderDeskAccessModal.css';

// Admin → User management → Order Desk access. A role (template) in one click, then per tab Off / View / Act,
// and per action a tick for special cases. Only the differences from the role are saved, so changing a role
// later changes everyone in it; the person keeps their own extras. Same rules as backend/services/orderDeskAccess.
const TEAM_NAMES = { office: 'Office', production: 'Production', warehouse: 'Warehouse', all: 'Everyone', manage: 'Manage' };
const GROUP_ORDER = ['office', 'production', 'warehouse', 'all', 'manage'];
const groupOf = (t) => t.group || t.team;
const LEVELS = [['off', 'Off'], ['view', 'View'], ['act', 'Act']];

export default function OrderDeskAccessModal({ user, getToken, onClose, onSaved }) {
  const [cat, setCat] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [legacy, setLegacy] = useState(false);
  const [role, setRole] = useState('none');
  const [tabsOver, setTabsOver] = useState({});
  const [actsOver, setActsOver] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const tokenRef = useRef(getToken); // the caller may pass a new function each render: load once per user
  tokenRef.current = getToken;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = await tokenRef.current();
        const h = { Authorization: `Bearer ${token}` };
        const [c, u] = await Promise.all([
          fetch('/api/v1/admin/order-desk-access/catalog', { headers: h }).then((r) => r.json()),
          fetch(`/api/v1/admin/order-desk-access/${encodeURIComponent(user.uid)}`, { headers: h }).then((r) => r.json()),
        ]);
        if (!c.success || !u.success) throw new Error(c.message || u.message || 'Could not load Order Desk access');
        if (!alive) return;
        setCat(c);
        setIsAdmin(!!u.isAdmin);
        setLegacy(!!u.stored?.legacy && (u.stored?.role || 'none') !== 'none' ? u.stored.role : false);
        setRole(u.stored?.role || 'none');
        setTabsOver(u.stored?.tabs || {});
        setActsOver(u.stored?.actions || {});
      } catch (e) { if (alive) setErr(e.message); }
    })();
    return () => { alive = false; };
  }, [user.uid]);

  const roleDef = useMemo(() => cat?.roles.find((r) => r.id === role) || null, [cat, role]);
  const roleTab = (id) => (roleDef ? roleDef.tabs[id] || 'off' : 'off');
  const tabLevel = (id) => tabsOver[id] || roleTab(id);
  const actDefault = (a) => tabLevel(a.tab) === 'act' && (!a.managerOnly || role === 'manager');
  const actOn = (a) => tabLevel(a.tab) === 'act' && (typeof actsOver[a.key] === 'boolean' ? actsOver[a.key] : actDefault(a));
  const custom = cat ? Object.keys(tabsOver).filter((k) => tabsOver[k] !== roleTab(k)).length + Object.keys(actsOver).length : 0;

  const pickRole = (id) => { setRole(id); setTabsOver({}); setActsOver({}); };
  const setTab = (id, lvl) => {
    setTabsOver((p) => { const n = { ...p }; if (lvl === roleTab(id)) delete n[id]; else n[id] = lvl; return n; });
    if (lvl !== 'act') setActsOver((p) => { const n = { ...p }; cat.actions.filter((a) => a.tab === id).forEach((a) => delete n[a.key]); return n; });
  };
  const setAct = (a, on) => setActsOver((p) => {
    const n = { ...p };
    const def = (tabLevel(a.tab) === 'act') && (!a.managerOnly || role === 'manager');
    if (on === def) delete n[a.key]; else n[a.key] = on;
    return n;
  });

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const token = await tokenRef.current();
      const r = await fetch(`/api/v1/admin/order-desk-access/${encodeURIComponent(user.uid)}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, tabs: tabsOver, actions: actsOver }),
      }).then((x) => x.json());
      if (!r.success) throw new Error(r.message || 'Save failed');
      onSaved?.();
      onClose();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const teams = cat ? [...new Set(cat.tabs.map(groupOf))].sort((a, b) => GROUP_ORDER.indexOf(a) - GROUP_ORDER.indexOf(b)) : [];
  return (
    <Modal isOpen onClose={onClose} contentClassName="permissions-modal-content oda-modal">
      <div className="oda-head">
        <h2>Order Desk access: {user.email}</h2>
        <p>Pick a role, then change single tabs or actions only for special cases. Changes apply the next time the person opens Order Desk.</p>
      </div>
      {err && <p className="oda-err" role="alert">{err}</p>}
      {!cat && !err && <p>Loading…</p>}
      {cat && isAdmin && <p className="oda-note">Admins always have full Order Desk access (Manager).</p>}
      {cat && !isAdmin && (
        <>
          {legacy && <p className="oda-note">Not set up here yet: shown from the old Order Desk permission. Save to switch this person to the new access.</p>}
          <div className="oda-roles" role="radiogroup" aria-label="Role">
            {cat.roles.map((r) => (
              <button key={r.id} type="button" role="radio" aria-checked={role === r.id} className={`oda-role${role === r.id ? ' on' : ''}`} onClick={() => pickRole(r.id)}>
                <b>{r.label}</b><small>{r.description}</small>
              </button>
            ))}
            <button type="button" role="radio" aria-checked={role === 'none'} className={`oda-role none${role === 'none' ? ' on' : ''}`} onClick={() => pickRole('none')}>
              <b>No access</b><small>Order Desk hidden</small>
            </button>
          </div>
          {role !== 'none' && (
            <div className="oda-tabs">
              {teams.map((team) => (
                <section key={team} className="oda-team" aria-label={`${TEAM_NAMES[team] || team} tabs`}>
                  <h3>{TEAM_NAMES[team] || team}</h3>
                  {cat.tabs.filter((t) => groupOf(t) === team).map((t) => {
                    const lvl = tabLevel(t.id);
                    const changed = tabsOver[t.id] && tabsOver[t.id] !== roleTab(t.id);
                    const acts = cat.actions.filter((a) => a.tab === t.id);
                    return (
                      <div key={t.id} className={`oda-tab${changed ? ' changed' : ''}`}>
                        <div className="oda-tab-row">
                          <span className="oda-tab-name">{t.label}{changed && <em title={`Role gives: ${roleTab(t.id)}`}> · changed</em>}</span>
                          <span className="oda-seg" role="radiogroup" aria-label={`${t.label} access`}>
                            {LEVELS.map(([v, label]) => <button key={v} type="button" role="radio" aria-checked={lvl === v} className={lvl === v ? `on ${v}` : ''} onClick={() => setTab(t.id, v)}>{label}</button>)}
                          </span>
                        </div>
                        {lvl === 'act' && acts.length > 0 && (
                          <div className="oda-acts">
                            {acts.map((a) => {
                              const on = actOn(a);
                              const special = typeof actsOver[a.key] === 'boolean';
                              return (
                                <label key={a.key} className={`oda-act${special ? ' changed' : ''}`}>
                                  <input type="checkbox" checked={on} onChange={(e) => setAct(a, e.target.checked)} />
                                  <span>{a.label}</span>
                                  {a.managerOnly && <small className="oda-mgr">manager</small>}
                                  {special && <small className="oda-special">special</small>}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </section>
              ))}
            </div>
          )}
        </>
      )}
      <div className="modal-actions oda-foot">
        <span className="oda-sum">{role === 'none' ? 'No access' : `${roleDef?.label || role}${custom ? ` · custom: ${custom} change${custom === 1 ? '' : 's'}` : ''}`}</span>
        <button onClick={onClose} className="action-button cancel-btn" disabled={saving}>Cancel</button>
        {!isAdmin && <button onClick={save} disabled={saving || !cat} className="action-button save-btn">{saving ? 'Saving…' : 'Save Order Desk access'}</button>}
      </div>
    </Modal>
  );
}
